// 3rd party imports
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';

// bernie
import { DialogRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { CaptchaStore, UserAuthStore } from 'stores';

// stores
import { Program, UserInfo } from 'models';

// ui
import { UitkBanner } from 'uitk-react-banner';
import { UitkDialog, UitkDialogContent, UitkDialogFooter } from 'uitk-react-dialog';
import { UitkTextInput } from 'uitk-react-input';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading } from 'uitk-react-text';
import { UitkLink } from 'uitk-react-link';
import { Validation } from '../../util/Validation';

export interface VerifyPartnerDialogProps {
  captcha?: CaptchaStore;
  dialogId: string;
  l10n?: ILocalization;
  program: Program;
  routeStore?: RouteStore;
  user: UserInfo;
  userAuth: UserAuthStore;
  updateProgramStatusVerified?(): any;
}

export const VerifyPartnerDialog: React.FunctionComponent<VerifyPartnerDialogProps> = ({
  dialogId,
  l10n,
  program,
  routeStore,
  user,
  userAuth,
  updateProgramStatusVerified,
}) => {
  const [formMessage] = React.useState(null);
  const [formErrorMessage, setFormErrorMessage] = React.useState(null);

  const { control, errors, handleSubmit } = useForm({ mode: 'onBlur' });

  const onDismiss = () => {
    routeStore.closeDialog();
  };

  const onTriggerVerifyPartner = handleSubmit(({ partnerId }) => {
    const cid = partnerId.trim();
    if (Validation.validateWithRegex(/^[0-9]{5,7}$/, cid)) {
      triggerVerifyPartner(cid);
    } else {
      setFormErrorMessage(l10n.formatText('programs.add_cid_dialog.form.validation.enter_valid_company_id'));
    }
  });

  const handleIfOkResponseCode = (response) => {
    if (response.status === 200 || response.status === 201) {
      if (response.body.affiliatePartnership && response.body.affiliatePartnership.partnershipStatus !== 'UNKNOWN') {
        updateProgramStatusVerified();
        routeStore.closeDialog();

        return true;
      } else {
        setFormErrorMessage(l10n.formatText('programs.cards.content.cj.status_unknown.company_id_not_found'));

        return true;
      }
    }

    return false;
  };

  const handleIfErrorCode = (response, prog, partnerId) => {
    if (response.body && response.body.errorCode) {
      if (response.body.errorCode === '4041') {
        invokeUpdateUserProgramRelationship(prog, partnerId, false);

        return true;
      }
      if (response.body.errorCode === '4001') {
        invokeUpdateUserProgramRelationship(prog, partnerId, true);

        return true;
      }
    }

    return false;
  };

  const invokeUpdateUserProgramRelationship = (prog, partnerId, existing) => {
    const { brand, posa, network } = prog;

    userAuth
      .updateUserProgramRelationship(brand, posa, network, partnerId, existing)
      .then((response) => {
        if (handleIfOkResponseCode(response)) {
          return;
        } else if (handleIfErrorCode(response, prog, partnerId)) {
          return;
        }
        if (response.status.toString().startsWith('40')) {
          setFormErrorMessage(l10n.formatText('programs.cards.content.cj.status_unknown.company_id_not_found'));
        } else {
          setFormErrorMessage(l10n.formatText('common.unknown_server_error'));
        }
      })
      .catch(() => {
        setFormErrorMessage(l10n.formatText('common.unknown_server_error'));
      });
  };

  const triggerVerifyPartner = (partnerId) => {
    const userId = user && user.email ? user.email : null;

    if (userId) {
      const userProgram = userAuth.userProgramRelationships
        ? userAuth.userProgramRelationships.body.filter((userProg) => {
            return (
              userProg.id.userId === userId && userProg.id.brand === program.brand && userProg.id.posa === program.posa
            );
          })
        : [];

      const existingPartnerId =
        userProgram.length > 0 &&
        userProgram[0].partnerIdentity &&
        userProgram[0].partnerIdentity.partnerId &&
        userProgram[0].partnerIdentity.partnerId !== null;

      invokeUpdateUserProgramRelationship(program, partnerId, existingPartnerId);
    } else {
      setFormErrorMessage(l10n.formatText('programs.add_cid_dialog.message.user_not_found'));
    }
  };

  // labels
  const partnerIdLabel = l10n.formatText('programs.add_cid_dialog.form.label.company_id');
  const partnerIdMissingErrorLabel = l10n.formatText('programs.add_cid_dialog.form.validation.enter_your_company_id');

  return (
    <>
      <DialogRoute id={dialogId}>
        <Layer id="dialogVerifyPartner" onDismiss={onDismiss}>
          <UitkDialog returnFocusOnClose={true}>
            <form name={'verifyPartnerForm'} onSubmit={onTriggerVerifyPartner} method="POST">
              <UitkDialogContent>
                <UitkHeading tag="h3" size={5}>
                  <LocalizedText message={'programs.add_cid_dialog.title.verify_approval_status'} />
                </UitkHeading>
                {formMessage && (
                  <UitkSpacing margin={{ block: 'six' }}>
                    <div>
                      <UitkBanner icon="info" message={formMessage} />
                    </div>
                  </UitkSpacing>
                )}
                {formErrorMessage && (
                  <UitkSpacing margin={{ block: 'six' }}>
                    <div>
                      <UitkErrorSummary heading={formErrorMessage} />
                    </div>
                  </UitkSpacing>
                )}
                <UitkSpacing margin={{ blockstart: 'six' }}>
                  <div>
                    <Controller
                      control={control}
                      name="partnerId"
                      defaultValue=""
                      rules={{
                        required: partnerIdMissingErrorLabel,
                      }}
                      render={({ onChange, onBlur, value, name }) => (
                        <UitkTextInput
                          id="verifyPartnerFormPartnerIdInput"
                          name={name}
                          label={partnerIdLabel}
                          value={value}
                          onChange={({ target: { value: newValue } }) => {
                            onChange(newValue);
                          }}
                          onBlur={onBlur}
                          invalid={errors.partnerId?.message}
                        />
                      )}
                    />
                  </div>
                </UitkSpacing>

                <UitkLink>
                  <a
                    href="https://egaffiliates.zendesk.com/hc/en-us/articles/360038186373-Where-to-find-CID"
                    target="_blank"
                  >
                    <LocalizedText message={'programs.add_cid_dialog.form.hint.how_to_find_this'} />
                  </a>
                </UitkLink>
              </UitkDialogContent>
              <UitkDialogFooter
                key="UitkDialogFooter"
                stacked={false}
                buttonOne={{
                  onClick: onTriggerVerifyPartner,
                  text: l10n.formatText('programs.add_cid_dialog.form.verify'),
                  type: 'primary',
                }}
              />
            </form>
          </UitkDialog>
        </Layer>
      </DialogRoute>
    </>
  );
};
